import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import AsyncSubmitButton, { ASYNC_SUBMIT_STATUS } from '../ui/AsyncSubmitButton';
import { receptionApiErrorMessage } from './receptionConstants';
import { useClasses } from './useReceptionData';

export default function ReceptionConvertDialog({ inquiryId, defaultClassId, onClose, onSuccess }) {
  const { toast } = useToast();
  const classes = useClasses();
  const [classId, setClassId] = useState(defaultClassId ? String(defaultClassId) : '');
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState('');
  const [submitStatus, setSubmitStatus] = useState(ASYNC_SUBMIT_STATUS.idle);

  useEffect(() => {
    if (!classId) { setSections([]); return; }
    api.get(`/reception/classes/${classId}/sections`)
      .then((res) => setSections(res.data?.data || []))
      .catch(() => setSections([]));
  }, [classId]);

  const convert = async ({ beginLoading }) => {
    if (!classId) {
      toast({ title: 'Class is required', variant: 'destructive' });
      return;
    }
    beginLoading();
    try {
      const res = await api.post(`/reception/inquiries/${inquiryId}/convert`, {
        class_id: parseInt(classId, 10),
        section_id: sectionId ? parseInt(sectionId, 10) : null,
      });
      toast({
        title: 'Converted to admission',
        description: res.data?.data?.temp_password
          ? `Student created. Temp password: ${res.data.data.temp_password}`
          : 'Student record created.',
      });
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
      throw e;
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Convert to Student Admission</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Select class and section for the new student record. Inquiry data will be pre-filled.</p>
        <div className="space-y-4 py-2">
          <div>
            <Label>Class *</Label>
            <Select value={classId || 'none'} onValueChange={(v) => setClassId(v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {classes.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {sections.length > 0 && (
            <div>
              <Label>Section</Label>
              <Select value={sectionId || 'none'} onValueChange={(v) => setSectionId(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select section" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {sections.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitStatus !== ASYNC_SUBMIT_STATUS.idle}>Cancel</Button>
          <AsyncSubmitButton
            onAction={convert}
            onSuccess={() => onSuccess?.()}
            onStatusChange={setSubmitStatus}
            loadingLabel="Converting…"
          >
            Convert
          </AsyncSubmitButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
